import React from "react"
import dayjs, { Dayjs } from "utils/dayjs"
import classNames from 'classnames';


import i18n from "intl/i18n"
import { formatNumber, getSupplierTypeLabel } from "utils/utils"
import { Guid, ISummarySection, Lmid } from "@/types"
import AmountWithCurrency, { IAmountWithCurrencyDto } from "./AmountWithCurrency"
import { InvoiceAssignmentStatus } from "./Invoice"
import NoValue from "components/NoValue/NoValue"
import Supplier, { IGetSupplierDto } from "./Supplier"
import { IListItem } from "components/List/List"
import Text from "components/Text/Text"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import reservationSidebars from 'stores/reservationSidebars';

export interface IInvoiceDetailsBasicDataDto {
    assignmentStatus: InvoiceAssignmentStatus,
    invoiceDate: string
    invoiceNumber: string
    requestNumber?: string
    requestId?: Guid
    isCustomerRequestDeleted: boolean
    invoiceAmount: IAmountWithCurrencyDto
    invoiceForeignCurrencyAmount?: IAmountWithCurrencyDto | null
    discountPercentage: number
    discountAmount: IAmountWithCurrencyDto
    discountForeignCurrencyAmount?: IAmountWithCurrencyDto | null
    amountLessDiscount: IAmountWithCurrencyDto
    amountForeignCurrencyLessDiscount?: IAmountWithCurrencyDto | null
    costCategoryName: string
    costCategoryLedgerAccount?: string | null
    referenceNumber: string
    referenceText?: string
    bookingPeriod: string
    supplierId?: IGetSupplierDto["id"] | null
    supplierNumber?: IGetSupplierDto["number"] | null
    supplierName?: IGetSupplierDto["number"] | null
    supplierType?: IGetSupplierDto["type"] | null
}

export interface IInvoiceDetailsAssignedAssetDto {
    id: Guid
    articleId: Guid
    isAssetLabeled: boolean
    articleDescription: string
    lmid: Lmid
    assignedAmount: IAmountWithCurrencyDto
    assignedForeignCurrencyAmount?: IAmountWithCurrencyDto
    version: number
    isCurrentVersion: boolean
}

export interface IGetInvoiceDetailsDto {
    basicData: IInvoiceDetailsBasicDataDto
    assets: IInvoiceDetailsAssignedAssetDto[]
}

export interface IInvoiceDetailsBasicData {
    assignmentStatus: InvoiceAssignmentStatus
    invoiceDate: Dayjs
    invoiceNumber: string
    requestNumber?: string
    requestId?: Guid
    isCustomerRequestDeleted: boolean
    invoiceAmount: AmountWithCurrency
    invoiceForeignCurrencyAmount?: AmountWithCurrency | null
    discountPercentage: number
    discountAmount: AmountWithCurrency
    discountForeignCurrencyAmount?: AmountWithCurrency | null
    amountLessDiscount: AmountWithCurrency
    amountForeignCurrencyLessDiscount?: AmountWithCurrency | null
    referenceNumber: string
    costCategoryName: string
    costCategoryLedgerAccount?: string | null
    referenceText?: string
    bookingPeriod: Dayjs
    supplierId?: Supplier["id"] | null
    supplierNumber?: Supplier["number"] | null
    supplierName?: Supplier["number"] | null
    supplierType?: Supplier["type"] | null
}

export interface IInvoiceDetailsAssignedAsset {
    id: Guid
    articleId: Guid
    isAssetLabeled: boolean
    articleDescription: string
    lmid: Lmid
    assignedAmount: AmountWithCurrency
    assignedForeignCurrencyAmount?: AmountWithCurrency
    version: number
    isCurrentVersion: boolean
}

export interface IInvoiceDetailsBasicDataSummary {
    basicData: IInvoiceDetailsBasicData
}

export default class InvoiceDetails {
    basicData: IInvoiceDetailsBasicData
    assets: IInvoiceDetailsAssignedAsset[]

    constructor({ basicData, assets }: IGetInvoiceDetailsDto) {
        this.basicData = {
            ...basicData,
            invoiceDate: dayjs(basicData.invoiceDate, "YYYY-MM-DD"),
            invoiceAmount: new AmountWithCurrency(basicData.invoiceAmount),
            invoiceForeignCurrencyAmount: basicData.invoiceForeignCurrencyAmount ? new AmountWithCurrency(basicData.invoiceForeignCurrencyAmount) : undefined,
            discountAmount: new AmountWithCurrency(basicData.discountAmount),
            discountForeignCurrencyAmount: basicData.discountForeignCurrencyAmount ? new AmountWithCurrency(basicData.discountForeignCurrencyAmount) : undefined,
            amountLessDiscount: new AmountWithCurrency(basicData.amountLessDiscount),
            amountForeignCurrencyLessDiscount: basicData.amountForeignCurrencyLessDiscount ? new AmountWithCurrency(basicData.amountForeignCurrencyLessDiscount) : undefined,
            bookingPeriod: dayjs(basicData.bookingPeriod, "YYYY-MM"),
        }

        this.assets = assets.map(asset => {
            return {
                articleDescription: asset.articleDescription,
                id: asset.id,
                articleId: asset.articleId,
                lmid: asset.lmid,
                isAssetLabeled: asset.isAssetLabeled,
                assignedAmount: new AmountWithCurrency(asset.assignedAmount),
                assignedForeignCurrencyAmount: asset.assignedForeignCurrencyAmount ? new AmountWithCurrency(asset.assignedForeignCurrencyAmount) : undefined,
                version: asset.version,
                isCurrentVersion: asset.isCurrentVersion,
            }
        })
    }

    getSummaryBasicData(): ISummarySection<IListItem>[] {
        return [
            {
                title: i18n.t('invoices.basicData'),
                items: [
                    {
                        label: i18n.t('invoices.assignmentStatus'),
                        value: this.basicData.assignmentStatus
                    },
                    {
                        label: i18n.t('invoices.costCategory'),
                        value: this.basicData.costCategoryName
                    },
                    {
                        label: i18n.t('invoices.requestNo'),
                        value: !this.basicData.requestNumber ? React.createElement(NoValue) :
                            this.basicData.isCustomerRequestDeleted ? React.createElement(FlexibleContent, {
                                text: this.basicData.requestNumber,
                                className: classNames(this.basicData.assignmentStatus === InvoiceAssignmentStatus.notAssigned
                                    && "color-red-500")
                            }) :
                                React.createElement(FlexibleContent, {
                                    text: this.basicData.requestNumber,
                                    onClick: () => reservationSidebars.show({
                                        reservationId: this.basicData.requestId!,
                                    })
                                })
                        ,
                    },
                    {
                        label: i18n.t('invoices.ledgerAccount'),
                        value: this.basicData.costCategoryLedgerAccount || React.createElement(NoValue),
                    },
                    {
                        label: i18n.t('invoices.supplier.label'),
                        value: this.basicData.supplierName ? `${this.basicData.supplierNumber} (${this.basicData.supplierName})` : React.createElement(NoValue),
                        subValue: this.basicData.supplierType ? (React.createElement(Text, { className: "color-grey-500", type: 'pSmallest' }, getSupplierTypeLabel(this.basicData.supplierType))) : undefined
                    },
                    {
                        label: i18n.t('invoices.bookingPeriod'),
                        value: this.basicData.bookingPeriod.format(i18n.t('config.dateMonthYearFormat'))
                    },
                    {
                        label: i18n.t('invoices.invoiceDate'),
                        value: this.basicData.invoiceDate.format(i18n.t('config.dateLongFormat'))
                    },
                    {
                        label: i18n.t('invoices.invoiceNumber'),
                        value: this.basicData.invoiceNumber
                    },
                    {
                        label: i18n.t('invoices.invoiceAmount'),
                        value: this.basicData.invoiceAmount.print(),
                        subValue: this.basicData.invoiceForeignCurrencyAmount ? (React.createElement(Text, { className: "color-grey-500", type: 'pSmallest' }, this.basicData.invoiceForeignCurrencyAmount.print())) : undefined
                    },
                    {
                        label: i18n.t('invoices.discount'),
                        value: `${formatNumber(this.basicData.discountPercentage, 2)} % (${this.basicData.discountAmount.print()})`,
                        subValue: this.basicData.discountForeignCurrencyAmount ? (React.createElement(Text, { className: "color-grey-500", type: 'pSmallest' }, this.basicData.discountForeignCurrencyAmount.print())) : undefined
                    },
                    {
                        label: i18n.t('invoices.invoiceAmountLessDiscount'),
                        value: this.basicData.amountLessDiscount.print(),
                        subValue: this.basicData.amountForeignCurrencyLessDiscount ? (React.createElement(Text, { className: "color-grey-500", type: 'pSmallest' }, this.basicData.amountForeignCurrencyLessDiscount.print())) : undefined
                    },
                    {
                        label: i18n.t('invoices.referenceNumber'),
                        value: this.basicData.referenceNumber
                    },
                    {
                        label: i18n.t('invoices.referenceText'),
                        value: this.basicData.referenceText || React.createElement(NoValue)
                    },
                ]
            }
        ]

    }
}
